import React, { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService, { setSignalRToken } from "../api/apiService";
import { localizedPath } from "../utils/routeHelpers";

export const AuthContext = createContext(null);

const readJson = (key, fallback) => {
    try {
        return JSON.parse(localStorage.getItem(key)) ?? fallback;
    } catch {
        return fallback;
    }
};

export const AuthProvider = ({ children }) => {
    // Optymistyczny render z cache localStorage; źródłem prawdy jest GET /users/me.
    const [user, setUser] = useState(() => readJson("user", null));
    const [permissions, setPermissions] = useState(() => readJson("permissions", []));
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const handleLoginSuccess = useCallback((accessToken, userData, userPermissions) => {
        // `companyId` zawsze ZDEFINIOWANE (null = brak powiązania z firmą) — to bramka
        // panelu firmy (/firma) i pozycji „Panel firmy" w menu. Odpowiedź /users/me sprzed
        // wdrożenia paneli firm (oraz stary cache w localStorage) nie zna tego pola, więc
        // normalizujemy tutaj zamiast rozsiewać `?? null` po ekranach.
        const normalizedUser = userData
            ? { ...userData, companyId: userData.companyId ?? null }
            : userData;
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        localStorage.setItem("permissions", JSON.stringify(userPermissions || []));
        setSignalRToken(accessToken);
        setUser(normalizedUser);
        setPermissions(userPermissions || []);
    }, []);

    useEffect(() => {
        const checkLoggedIn = async () => {
            if (!localStorage.getItem("user")) {
                setLoading(false);
                return;
            }
            try {
                const res = await apiService.getMe();
                const { accessToken, user: userData, permissions: userPermissions } = res.data;
                handleLoginSuccess(accessToken, userData, userPermissions);
            } catch {
                // 401 obsłuży interceptor (cichy refresh / redirect); błąd sieci = zostaje cache.
            } finally {
                setLoading(false);
            }
        };
        checkLoggedIn();
    }, [handleLoginSuccess]);

    // Interceptor wyczyścił localStorage — zeruj też stan Reacta, żeby chroniony UI zniknął.
    useEffect(() => {
        const handleSessionCleared = () => {
            setUser(null);
            setPermissions([]);
        };
        window.addEventListener("auth-session-cleared", handleSessionCleared);
        return () => window.removeEventListener("auth-session-cleared", handleSessionCleared);
    }, []);

    /**
     * Logowanie po dłuższej przerwie kończy się WYZWANIEM SMS zamiast sesji: backend zwraca
     * `{ requiresSmsVerification: true, challengeId, maskedPhone }` i ŻADNYCH tokenów.
     * Dlatego obie metody logowania zwracają teraz wynik, a nie „nic": ekran logowania musi
     * odróżnić „jesteś w środku" od „podaj kod z SMS-a". Bez tego rozróżnienia strona
     * przekierowywałaby na pulpit z pustą sesją.
     */
    const finishOrChallenge = useCallback((data, redirectTo) => {
        if (data?.requiresSmsVerification) {
            return {
                requiresSmsVerification: true,
                challengeId: data.challengeId,
                maskedPhone: data.maskedPhone,
                expiresInSeconds: data.expiresInSeconds,
                attemptsLeft: data.attemptsLeft,
                message: data.message,
                devCode: data.devCode || null,
            };
        }
        const { accessToken, user: userData, permissions: userPermissions } = data;
        handleLoginSuccess(accessToken, userData, userPermissions);
        // Konta zewnętrzne (portal) lądują w swoim profilu portalowym, a nie w panelu obsługi —
        // chyba że wracamy do konkretnego celu (np. link z kodu QR w `redirectTo`).
        // Wizytator po zalogowaniu trafia na PULPIT — to jego jedyny dom w aplikacji
        // (decyzja klienta 2026-08-08: osobny profil portalowy zlikwidowany).
        navigate(redirectTo || "/");
        return { requiresSmsVerification: false };
    }, [handleLoginSuccess, navigate]);

    const login = useCallback(async (formData, redirectTo) => {
        const res = await apiService.login(formData);
        return finishOrChallenge(res.data, redirectTo);
    }, [finishOrChallenge]);

    const loginOffice365 = useCallback(async (idToken, redirectTo) => {
        const res = await apiService.loginOffice365({ idToken });
        return finishOrChallenge(res.data, redirectTo);
    }, [finishOrChallenge]);

    /** Drugi krok logowania: kod z SMS-a. Dopiero tu powstaje sesja. */
    const verifyLoginSms = useCallback(async (challengeId, code, redirectTo) => {
        const res = await apiService.verifyLoginSms(challengeId, code);
        const { accessToken, user: userData, permissions: userPermissions } = res.data;
        handleLoginSuccess(accessToken, userData, userPermissions);
        // Wizytator po zalogowaniu trafia na PULPIT — to jego jedyny dom w aplikacji
        // (decyzja klienta 2026-08-08: osobny profil portalowy zlikwidowany).
        navigate(redirectTo || "/");
    }, [handleLoginSuccess, navigate]);

    /** „Wyślij ponownie" — serwer wydaje NOWE wyzwanie, więc stary challengeId trzeba podmienić. */
    const resendLoginSms = useCallback(async (challengeId) => {
        const res = await apiService.resendLoginSms(challengeId);
        return {
            challengeId: res.data?.challengeId,
            maskedPhone: res.data?.maskedPhone,
            expiresInSeconds: res.data?.expiresInSeconds,
            attemptsLeft: res.data?.attemptsLeft,
            devCode: res.data?.devCode || null,
        };
    }, []);

    const refreshSession = useCallback(async () => {
        try {
            const res = await apiService.getMe();
            const { accessToken, user: userData, permissions: userPermissions } = res.data;
            handleLoginSuccess(accessToken, userData, userPermissions);
        } catch {
            // interceptor zajmie się 401
        }
    }, [handleLoginSuccess]);

    /**
     * Wylogowanie. `redirectTo` pozwala wskazać, DOKĄD wrócić — portal odsyła gościa na
     * `/wniosek`, bo ekran logowania pracowniczego (z Office 365) nie jest dla niego żadnym
     * zakończeniem sesji, tylko ślepym zaułkiem z formularzem, do którego nie ma poświadczeń.
     */
    const logout = useCallback(async (redirectTo) => {
        try {
            await apiService.logout();
        } catch {
            // backend czyści cookies; lokalnie czyścimy zawsze
        }
        localStorage.removeItem("user");
        localStorage.removeItem("permissions");
        setSignalRToken(null);
        setUser(null);
        setPermissions([]);
        // Przyjmujemy WYŁĄCZNIE ścieżki wewnętrzne — `redirectTo` bywa wołane z obsługi zdarzenia,
        // gdzie argumentem jest obiekt zdarzenia, a nie adres.
        const target =
            typeof redirectTo === "string" && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
                ? redirectTo
                : localizedPath("login");
        navigate(target);
    }, [navigate]);

    const hasPermission = useCallback((required) => {
        if (!required) return true;
        if (user?.type === 3 || permissions.includes("ALL_ACCESS")) return true;
        if (Array.isArray(required)) {
            return required.some((perm) => permissions.includes(perm));
        }
        return permissions.includes(required);
    }, [user, permissions]);

    return (
        <AuthContext.Provider
            value={{
                user,
                permissions,
                loading,
                login,
                loginOffice365,
                verifyLoginSms,
                resendLoginSms,
                logout,
                refreshSession,
                hasPermission,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
